import React, { useEffect, useRef, useState } from 'react';
import { BEFORE_WORKSPACE_LAYOUT_CHANGE } from '../utils/workspaceLayout';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Modal, SpaceBetween } from '@cloudscape-design/components';
import useCurrentUser from './useCurrentUser';
import { readWorkQueueOrigin, workQueueActor, workQueueReturnPath } from '../utils/workQueueReturn';

export default function useWorkQueueReturn(role, hasUnsavedLetterDrafts = false, reviewStage = null, hasUnsavedAssessment = false, options = {}) {
  const history = useHistory();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const [confirmExit, setConfirmExit] = useState(false);
  const { busy = false, onDiscard, widgetId } = options;
  const pendingLayoutChange = useRef(null);
  useEffect(() => {
    if (!widgetId) return undefined;
    const handler = event => {
      if (!event.detail?.removedWidgetIds?.includes(widgetId)) return;
      if (!busy && !hasUnsavedLetterDrafts && !hasUnsavedAssessment) return;
      event.preventDefault();
      if (busy) return;
      pendingLayoutChange.current = event.detail.proceed;
      setConfirmExit(true);
    };
    window.addEventListener(BEFORE_WORKSPACE_LAYOUT_CHANGE, handler);
    return () => window.removeEventListener(BEFORE_WORKSPACE_LAYOUT_CHANGE, handler);
  }, [busy, hasUnsavedLetterDrafts, hasUnsavedAssessment, widgetId]);
  const leave = (discard = false) => {
    if (busy) return;
    if (discard) onDiscard?.();
    setConfirmExit(false);
    if (pendingLayoutChange.current) {
      const proceed = pendingLayoutChange.current;
      pendingLayoutChange.current = null;
      proceed();
      return;
    }
    history.push(workQueueReturnPath(readWorkQueueOrigin(location.search, workQueueActor(currentUser, role))));
  };
  return {
    returnToWorkQueue: () => {
      if (busy) return;
      pendingLayoutChange.current = null;
      if (hasUnsavedLetterDrafts || hasUnsavedAssessment) setConfirmExit(true);
      else leave();
    },
    // For already-saved exceptional exits, e.g. a declared conflict.
    returnAfterSavedAction: () => leave(),
    returnConfirmation: <Modal
      visible={confirmExit}
      header={hasUnsavedAssessment ? 'Leave assessment?' : 'Leave letter drafts?'}
      closeAriaLabel="Keep working"
      onDismiss={() => { pendingLayoutChange.current = null; setConfirmExit(false); }}
      footer={<SpaceBetween direction="horizontal" size="xs">
        <Button onClick={() => { pendingLayoutChange.current = null; setConfirmExit(false); }}>Keep working</Button>
        <Button variant="primary" disabled={busy} onClick={() => leave(true)}>Leave without saving</Button>
      </SpaceBetween>}
    >Unsaved changes will be lost.</Modal>,
  };
}
